import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Users, IndianRupee, ArrowUpRight, ArrowDownRight, Search,
  Plus, Settings, MoreVertical, ArrowUpDown, ChevronDown,
  ArrowLeft, Layers, FileText, Edit2, Trash2, Link2
} from "lucide-react";
import CreatePartyModal from "@/components/CreatePartyModal";

const PartiesPage = ({ setIsAuthenticated }) => {
  const navigate = useNavigate();
  const [parties, setParties] = useState([]);
  const [filteredParties, setFilteredParties] = useState([]);
  const [summary, setSummary] = useState({ total_parties: 0, to_collect: 0, to_pay: 0 });
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCard, setActiveCard] = useState("all");
  const [sortField, setSortField] = useState("party_name");
  const [sortDir, setSortDir] = useState("asc");
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingParty, setEditingParty] = useState(null);

  const fetchParties = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/parties`);
      setParties(response.data);
    } catch (error) {
      toast.error("Failed to fetch parties");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSummary = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/parties/summary`);
      setSummary(response.data);
    } catch (error) {
      console.error("Failed to fetch summary:", error);
    }
  }, []);

  const filterAndSort = useCallback(() => {
    let filtered = [...parties];

    if (activeCard === "to_collect") {
      filtered = filtered.filter(p => p.balance_type === "to_collect" && p.opening_balance > 0);
    } else if (activeCard === "to_pay") {
      filtered = filtered.filter(p => p.balance_type === "to_pay" && p.opening_balance > 0);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(p =>
        p.party_name.toLowerCase().includes(q) ||
        (p.mobile_number && p.mobile_number.includes(q))
      );
    }

    filtered.sort((a, b) => {
      let valA, valB;
      if (sortField === "party_name") {
        valA = a.party_name.toLowerCase();
        valB = b.party_name.toLowerCase();
      } else if (sortField === "balance") {
        valA = a.opening_balance || 0;
        valB = b.opening_balance || 0;
      } else {
        valA = a[sortField] || "";
        valB = b[sortField] || "";
      }
      if (valA < valB) return sortDir === "asc" ? -1 : 1;
      if (valA > valB) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

    setFilteredParties(filtered);
  }, [parties, searchQuery, activeCard, sortField, sortDir]);

  useEffect(() => { fetchParties(); fetchSummary(); }, [fetchParties, fetchSummary]);
  useEffect(() => { filterAndSort(); }, [filterAndSort]);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const handleDelete = async (partyId) => {
    if (!window.confirm("Are you sure you want to delete this party?")) return;
    try {
      await axios.delete(`${API}/parties/${partyId}`);
      toast.success("Party deleted");
      fetchParties();
      fetchSummary();
    } catch (error) {
      toast.error("Failed to delete party");
    }
  };

  const handleModalClose = (refresh) => {
    setModalOpen(false);
    setEditingParty(null);
    if (refresh) {
      fetchParties();
      fetchSummary();
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);
  };

  const getBalanceDisplay = (party) => {
    if (party.opening_balance === 0) return { text: formatCurrency(0), color: "text-slate-600" };
    if (party.balance_type === "to_collect") return { text: formatCurrency(party.opening_balance), color: "text-green-600" };
    if (party.balance_type === "to_pay") return { text: formatCurrency(party.opening_balance), color: "text-red-600" };
    return { text: formatCurrency(party.opening_balance), color: "text-slate-600" };
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-[22px] font-bold text-[#1E293B]">Customers</h1>
            <p className="text-[13px] text-[#94A3B8]">Manage your customers and vendors</p>
          </div>
            <div className="flex items-center gap-2">
              <Button data-testid="shared-ledger-btn" variant="outline" size="sm" className="text-[#64748B] border-[#E2E8F0] gap-1.5 text-[12px]">
                <Link2 className="w-4 h-4" />
                SharedLedger Portal
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button data-testid="reports-dropdown" variant="outline" size="sm" className="text-[#3B82F6] border-[#E2E8F0] gap-1.5 text-[12px]">
                    <FileText className="w-4 h-4" />
                    Reports
                    <ChevronDown className="w-3 h-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem>Party Statement</DropdownMenuItem>
                  <DropdownMenuItem>Outstanding Report</DropdownMenuItem>
                  <DropdownMenuItem>Party Wise Profit</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button variant="ghost" size="icon" className="text-[#94A3B8]">
                <Settings className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

      {/* Main Content */}
      <div>
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div
            data-testid="card-all-parties"
            className={`bg-white rounded-2xl p-5 cursor-pointer transition-all shadow-sm border-2 ${activeCard === 'all' ? 'border-[#3B82F6]' : 'border-[#F1F5F9] hover:border-[#E2E8F0]'}`}
            onClick={() => setActiveCard("all")}
          >
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-[#3B82F6]" />
              <span className="text-[12px] font-semibold text-[#3B82F6]">All Customers</span>
            </div>
            <p className="text-3xl font-bold text-[#1E293B]">{summary.total_parties}</p>
          </div>

          <div
            data-testid="card-to-collect"
            className={`bg-white rounded-2xl p-5 cursor-pointer transition-all shadow-sm border-2 ${activeCard === 'to_collect' ? 'border-[#4CAF50]' : 'border-[#F1F5F9] hover:border-[#E2E8F0]'}`}
            onClick={() => setActiveCard("to_collect")}
          >
            <div className="flex items-center gap-2 mb-2">
              <ArrowUpRight className="w-4 h-4 text-[#4CAF50]" />
              <span className="text-[12px] font-semibold text-[#4CAF50]">To Collect</span>
            </div>
            <p className="text-3xl font-bold text-[#1E293B]">
              {formatCurrency(summary.to_collect)}
            </p>
          </div>

          <div
            data-testid="card-to-pay"
            className={`bg-white rounded-2xl p-5 cursor-pointer transition-all shadow-sm border-2 ${activeCard === 'to_pay' ? 'border-[#EF4444]' : 'border-[#F1F5F9] hover:border-[#E2E8F0]'}`}
            onClick={() => setActiveCard("to_pay")}
          >
            <div className="flex items-center gap-2 mb-2">
              <ArrowDownRight className="w-4 h-4 text-[#EF4444]" />
              <span className="text-[12px] font-semibold text-[#EF4444]">To Pay</span>
            </div>
            <p className="text-3xl font-bold text-[#1E293B]">
              {formatCurrency(summary.to_pay)}
            </p>
          </div>
        </div>

        {/* Search & Actions Bar */}
        <div className="flex items-center justify-between mb-4 gap-4">
          <div className="flex items-center gap-3 flex-1">
            <div className="relative max-w-xs flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                data-testid="search-party-input"
                placeholder="Search Customer"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-10"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="bulk-action-btn" variant="outline" size="sm" className="gap-1.5 text-slate-600">
                  <Layers className="w-4 h-4" />
                  Bulk Action
                  <ChevronDown className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem>Delete Selected</DropdownMenuItem>
                <DropdownMenuItem>Export Selected</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              data-testid="create-party-button"
              onClick={() => { setEditingParty(null); setModalOpen(true); }}
              className="bg-[#3B82F6] hover:bg-[#2563EB] text-white gap-1.5 text-[13px] font-semibold rounded-lg"
            >
              <Plus className="w-4 h-4" />
              Create Customer
            </Button>
          </div>
        </div>

        {/* Parties Table */}
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto max-h-[calc(100vh-380px)] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead
                    className="font-semibold text-slate-700 cursor-pointer select-none"
                    onClick={() => toggleSort("party_name")}
                  >
                    <span className="flex items-center gap-1">
                      Customer Name
                      <ArrowUpDown className="w-3 h-3 text-slate-400" />
                    </span>
                  </TableHead>
                  <TableHead className="font-semibold text-slate-700">Category</TableHead>
                  <TableHead className="font-semibold text-slate-700">Mobile Number</TableHead>
                  <TableHead className="font-semibold text-slate-700">Customer type</TableHead>
                  <TableHead
                    className="font-semibold text-slate-700 cursor-pointer select-none"
                    onClick={() => toggleSort("balance")}
                  >
                    <span className="flex items-center gap-1">
                      Balance
                      <ArrowUpDown className="w-3 h-3 text-slate-400" />
                    </span>
                  </TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12 text-slate-500">
                      Loading parties...
                    </TableCell>
                  </TableRow>
                ) : filteredParties.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12">
                      <Users className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                      <p className="text-slate-500">
                        {searchQuery ? "No customers match your search" : "No customers yet. Create your first customer."}
                      </p>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredParties.map((party) => {
                    const bal = getBalanceDisplay(party);
                    return (
                      <TableRow
                        key={party.id}
                        data-testid={`party-row-${party.id}`}
                        className="hover:bg-slate-50"
                      >
                        <TableCell>
                          <button
                            data-testid={`party-name-link-${party.id}`}
                            onClick={() => navigate(`/parties/${party.id}`)}
                            className="text-slate-900 hover:text-indigo-600 hover:underline font-medium cursor-pointer text-left"
                          >
                            {party.party_name}
                          </button>
                        </TableCell>
                        <TableCell className="text-slate-500">{party.party_category || "-"}</TableCell>
                        <TableCell className="text-slate-600">{party.mobile_number || "-"}</TableCell>
                        <TableCell className="text-slate-600">{party.party_type}</TableCell>
                        <TableCell className={bal.color}>
                          {bal.text}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                data-testid={`party-actions-${party.id}`}
                                variant="ghost" size="icon"
                                className="h-8 w-8 text-slate-400 hover:text-slate-600"
                              >
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => navigate(`/parties/${party.id}`)}>
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => { setEditingParty(party); setModalOpen(true); }}>
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleDelete(party.id)}
                                className="text-red-600"
                              >
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      <CreatePartyModal
        open={modalOpen}
        onClose={handleModalClose}
        editingParty={editingParty}
      />
    </div>
  );
};

export default PartiesPage;
